import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  increment 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Embedded Analytics Service - JavaScript snippets for page view tracking
 */
const embeddedAnalyticsService = {
  /**
   * Generate tracking pixel and JavaScript snippet for influencers
   * @param {string} trackingCode - Unique tracking code
   * @param {string} campaignId - Campaign ID
   * @param {string} influencerId - Influencer ID
   * @returns {object} - Tracking snippets and instructions
   */
  generateTrackingSnippets: (trackingCode, campaignId, influencerId) => {
    const baseUrl = window.location.origin;
    
    // 1x1 transparent tracking pixel
    const trackingPixel = `<img src="${baseUrl}/api/track/pixel/${trackingCode}" width="1" height="1" style="display:none;" alt="" />`;
    
    // JavaScript tracking snippet
    const jsSnippet = `
<script>
(function() {
  // Engaged Manila Analytics
  var trackingCode = '${trackingCode}';
  var apiUrl = '${baseUrl}/api/track';
  
  // Track page view
  function trackPageView() {
    var data = {
      trackingCode: trackingCode,
      type: 'pageview',
      url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      screenResolution: screen.width + 'x' + screen.height,
      language: navigator.language,
      platform: navigator.platform
    };
    
    // Send tracking data
    if (navigator.sendBeacon) {
      navigator.sendBeacon(apiUrl + '/event', JSON.stringify(data));
    } else {
      fetch(apiUrl + '/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      }).catch(function(e) { /* Tracking failed silently */ });
    }
  }
  
  // Track scroll depth
  var maxScroll = 0;
  function trackScroll() {
    var scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
    if (scrollPercent > maxScroll && scrollPercent % 25 === 0) {
      maxScroll = scrollPercent;
      
      var data = {
        trackingCode: trackingCode,
        type: 'scroll',
        scrollDepth: scrollPercent,
        timestamp: new Date().toISOString()
      };
      
      if (navigator.sendBeacon) {
        navigator.sendBeacon(apiUrl + '/event', JSON.stringify(data));
      }
    }
  }
  
  // Track time on page
  var startTime = Date.now();
  function trackTimeOnPage() {
    var timeSpent = Math.round((Date.now() - startTime) / 1000);
    
    var data = {
      trackingCode: trackingCode,
      type: 'engagement',
      timeOnPage: timeSpent,
      timestamp: new Date().toISOString()
    };
    
    if (navigator.sendBeacon) {
      navigator.sendBeacon(apiUrl + '/event', JSON.stringify(data));
    }
  }
  
  // Initialize tracking
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackPageView);
  } else {
    trackPageView();
  }
  
  // Track scroll events (throttled)
  var scrollTimeout;
  window.addEventListener('scroll', function() {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(trackScroll, 100);
  });
  
  // Track time on page when leaving
  window.addEventListener('beforeunload', trackTimeOnPage);
  
  // Track visibility changes (tab switching)
  document.addEventListener('visibilitychange', function() {
    if (document.hidden) {
      trackTimeOnPage();
    }
  });
})();
</script>`;

    // Minimal tracking snippet (for character-limited scenarios)
    const minimalSnippet = `<script src="${baseUrl}/js/track.js?c=${trackingCode}"></script>`;
    
    // WordPress shortcode
    const wordpressShortcode = `[engaged_manila_tracking code="${trackingCode}"]`;
    
    return {
      trackingPixel,
      jsSnippet,
      minimalSnippet,
      wordpressShortcode,
      instructions: embeddedAnalyticsService.generateEmbeddedInstructions(trackingCode)
    };
  },

  /**
   * Generate comprehensive instructions for embedded analytics
   * @param {string} trackingCode - Tracking code
   * @returns {object} - Platform-specific instructions
   */
  generateEmbeddedInstructions: (trackingCode) => {
    return {
      general: {
        title: "Embedded Analytics - Advanced Tracking",
        description: "These snippets provide detailed analytics including page views, scroll depth, time on page, and user engagement metrics.",
        benefits: [
          "Track page views and impressions",
          "Measure scroll depth and engagement",
          "Monitor time spent on content",
          "Capture detailed user behavior data",
          "Works on any website or blog"
        ]
      },
      platforms: {
        wordpress: {
          title: "WordPress Integration",
          steps: [
            "Go to your WordPress admin dashboard",
            "Navigate to Appearance > Theme Editor",
            "Edit your theme's header.php file",
            "Add the JavaScript snippet before the closing </head> tag",
            "Save changes and test the tracking"
          ],
          alternative: "Use a plugin like 'Insert Headers and Footers' to add the code without editing theme files",
          shortcode: "You can also use this shortcode in posts/pages"
        },
        blogger: {
          title: "Blogger/Blogspot Integration",
          steps: [
            "Go to your Blogger dashboard",
            "Click on 'Theme' in the left sidebar",
            "Click 'Edit HTML'",
            "Find the </head> tag",
            "Paste the JavaScript snippet just before </head>",
            "Save the theme"
          ]
        },
        squarespace: {
          title: "Squarespace Integration",
          steps: [
            "Go to your Squarespace dashboard",
            "Navigate to Settings > Advanced > Code Injection",
            "Paste the JavaScript snippet in the 'Header' section",
            "Save changes"
          ]
        },
        wix: {
          title: "Wix Integration",
          steps: [
            "Go to your Wix dashboard",
            "Click on 'Settings' in the left panel",
            "Select 'Tracking & Analytics'",
            "Click '+ New Tool' and select 'Custom'",
            "Paste the JavaScript snippet",
            "Apply to all pages"
          ]
        },
        shopify: {
          title: "Shopify Integration",
          steps: [
            "Go to your Shopify admin",
            "Navigate to Online Store > Themes",
            "Click 'Actions' > 'Edit code'",
            "Open theme.liquid file",
            "Add the snippet before </head>",
            "Save the file"
          ]
        },
        html: {
          title: "Custom HTML/Website",
          steps: [
            "Open your website's HTML files",
            "Locate the <head> section",
            "Paste the JavaScript snippet before </head>",
            "Upload the updated files to your server"
          ]
        },
        email: {
          title: "Email Newsletter Tracking",
          instructions: "Use the tracking pixel in email newsletters",
          note: "JavaScript won't work in emails, but the 1x1 pixel will track opens"
        }
      },
      testing: {
        title: "Testing Your Implementation",
        steps: [
          "Visit your website/blog after implementing the code",
          "Check your browser's developer console for any errors",
          "Verify tracking data appears in your Engaged Manila analytics",
          "Test on different devices and browsers"
        ],
        troubleshooting: [
          "Ensure the snippet is placed in the <head> section",
          "Check that your website allows external scripts",
          "Verify there are no JavaScript errors blocking execution",
          "Test with ad blockers disabled"
        ]
      }
    };
  },

  /**
   * Process tracking event from embedded analytics
   * @param {object} eventData - Event data from JavaScript snippet
   * @returns {Promise<void>}
   */
  processTrackingEvent: async (eventData) => {
    try {
      const { trackingCode, type, ...data } = eventData;
      
      // Find the tracking link
      const trackingLinksRef = collection(db, 'trackingLinks');
      const q = query(trackingLinksRef, where('trackingCode', '==', trackingCode));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.warn('Tracking code not found:', trackingCode);
        return;
      }
      
      const trackingLinkDoc = querySnapshot.docs[0];
      const trackingLinkData = trackingLinkDoc.data();
      
      // Store the event
      await embeddedAnalyticsService.storeTrackingEvent({
        trackingLinkId: trackingLinkDoc.id,
        campaignId: trackingLinkData.campaignId,
        influencerId: trackingLinkData.influencerId,
        trackingCode,
        eventType: type,
        eventData: data,
        timestamp: serverTimestamp()
      });
      
      // Update tracking link analytics
      await embeddedAnalyticsService.updateTrackingAnalytics(trackingLinkDoc.ref, type, data);
      
    } catch (error) {
      console.error('Error processing tracking event:', error);
    }
  },

  /**
   * Store tracking event in database
   * @param {object} eventData - Event data to store
   * @returns {Promise<void>}
   */
  storeTrackingEvent: async (eventData) => {
    try {
      const eventsRef = collection(db, 'trackingEvents');
      await addDoc(eventsRef, eventData);
    } catch (error) {
      console.error('Error storing tracking event:', error);
    }
  },

  /**
   * Update tracking link analytics based on event type
   * @param {object} trackingLinkRef - Firestore document reference
   * @param {string} eventType - Type of event (pageview, scroll, engagement)
   * @param {object} eventData - Event data
   * @returns {Promise<void>}
   */
  updateTrackingAnalytics: async (trackingLinkRef, eventType, eventData) => {
    try {
      const updates = {};
      
      switch (eventType) {
        case 'pageview':
          updates['analytics.pageViews'] = increment(1);
          updates['analytics.lastPageView'] = serverTimestamp();
          break;
          
        case 'scroll':
          if (eventData.scrollDepth >= 50) {
            updates['analytics.engagedViews'] = increment(1);
          }
          if (eventData.scrollDepth >= 75) {
            updates['analytics.deepEngagement'] = increment(1);
          }
          break;
          
        case 'engagement':
          if (eventData.timeOnPage >= 30) {
            updates['analytics.qualityViews'] = increment(1);
          }
          updates['analytics.totalTimeOnPage'] = increment(eventData.timeOnPage || 0);
          break;
      }
      
      if (Object.keys(updates).length > 0) {
        await updateDoc(trackingLinkRef, updates);
      }
      
    } catch (error) {
      console.error('Error updating tracking analytics:', error);
    }
  },

  /**
   * Generate the minimal tracking script file content
   * @param {string} trackingCode - Tracking code
   * @returns {string} - JavaScript file content
   */
  generateMinimalTrackingScript: (trackingCode) => {
    return `
// Engaged Manila Minimal Tracking Script
(function(c) {
  var apiUrl = '${window.location.origin}/api/track';
  
  // Track page view
  function track() {
    var data = {
      trackingCode: c,
      type: 'pageview',
      url: location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString()
    };
    
    if (navigator.sendBeacon) {
      navigator.sendBeacon(apiUrl + '/event', JSON.stringify(data));
    } else {
      var img = new Image();
      img.src = apiUrl + '/pixel/' + c + '?url=' + encodeURIComponent(location.href);
    }
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', track);
  } else {
    track();
  }
})('${trackingCode}');
`;
  },

  /**
   * Get embedded analytics summary for a campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Analytics summary
   */
  getEmbeddedAnalytics: async (campaignId) => {
    try {
      const eventsRef = collection(db, 'trackingEvents');
      const q = query(eventsRef, where('campaignId', '==', campaignId));
      const querySnapshot = await getDocs(q);
      
      const analytics = {
        totalEvents: 0,
        pageViews: 0,
        engagedViews: 0,
        deepEngagement: 0,
        qualityViews: 0,
        totalTimeOnPage: 0,
        averageTimeOnPage: 0,
        eventsByType: {},
        eventsByInfluencer: {}
      };
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        analytics.totalEvents++;
        
        // Count by event type
        analytics.eventsByType[data.eventType] = (analytics.eventsByType[data.eventType] || 0) + 1;
        
        // Count by influencer
        analytics.eventsByInfluencer[data.influencerId] = (analytics.eventsByInfluencer[data.influencerId] || 0) + 1;
        
        // Aggregate specific metrics
        switch (data.eventType) {
          case 'pageview':
            analytics.pageViews++;
            break;
          case 'scroll':
            if (data.eventData.scrollDepth >= 50) analytics.engagedViews++;
            if (data.eventData.scrollDepth >= 75) analytics.deepEngagement++;
            break;
          case 'engagement':
            if (data.eventData.timeOnPage >= 30) analytics.qualityViews++;
            analytics.totalTimeOnPage += data.eventData.timeOnPage || 0;
            break;
        }
      });
      
      // Calculate average time on page
      if (analytics.qualityViews > 0) {
        analytics.averageTimeOnPage = Math.round(analytics.totalTimeOnPage / analytics.qualityViews);
      }
      
      return analytics;
      
    } catch (error) {
      console.error('Error getting embedded analytics:', error);
      return {
        totalEvents: 0,
        pageViews: 0,
        engagedViews: 0,
        deepEngagement: 0,
        qualityViews: 0,
        totalTimeOnPage: 0,
        averageTimeOnPage: 0,
        eventsByType: {},
        eventsByInfluencer: {}
      };
    }
  }
};

export default embeddedAnalyticsService;
